<!-- Sheets List with Tabs -->
<template>
  <div class="container-fluid py-4">
    <!-- Header with Tabs and Create Button -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'default' }"
            @click="setActiveTab('default')"
            type="button"
          >
            Default
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'byusers' }"
            @click="setActiveTab('byusers')"
            type="button"
          >
            By Users
          </button>
        </li>
      </ul>
      
      <!-- Create Button - Only show for Default tab -->
      <button
        v-if="activeTab === 'default'"
        class="btn btn-primary"
        @click="openCreateModal"
        :disabled="!canCreate('main')"
      >
        <i class="fa fa-plus me-2"></i>
        Create New Sheet
      </button>
    </div>

    <!-- Tab Content -->
    <div class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">
          {{ currentTabTitle }}
        </h5>
      </div>
      
      <div class="card-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">Loading {{ activeTab === 'default' ? 'Default' : 'By Users' }} sheets...</p>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          <div class="d-flex align-items-center">
            <i class="fa fa-exclamation-triangle me-2"></i>
            <div class="flex-grow-1">
              <strong>Error:</strong> {{ error }}
            </div>
            <button class="btn btn-sm btn-outline-danger" @click="fetchSheets">
              <i class="fa fa-refresh"></i> Retry
            </button>
          </div>
        </div>
        
        <!-- Sheets Table -->
        <div v-else-if="sheets.length > 0">
          <CommonTable2
            :tableObj="tableConfig"
            :title="''"
            permissionKey="main"
            @edit="handleEdit"
            @delete="handleDelete"
            @view="handleView"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-5">
          <div class="mb-3">
            <i class="fa fa-file-alt fa-3x text-muted"></i>
          </div>
          <h5 class="text-muted">No {{ activeTab === 'default' ? 'Default' : 'By Users' }} Sheets Found</h5>
          <p class="text-muted">
            {{ activeTab === 'default' 
              ? 'No default sheets are available.' 
              : 'No By Users sheets are available.' 
            }}
          </p>
          <div class="mt-3">
            <button 
              v-if="activeTab === 'default'"
              class="btn btn-primary me-2" 
              @click="openCreateModal"
              :disabled="!canCreate('main')"
            >
              <i class="fa fa-plus me-1"></i>
              Create Sheet
            </button>
            <button class="btn btn-outline-secondary" @click="fetchSheets">
              <i class="fa fa-refresh me-1"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Footer -->
      <div v-if="sheets.length > 0" class="card-footer bg-light">
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">
            Showing {{ sheets.length }} {{ activeTab === 'default' ? 'default' : 'By Users' }} sheet{{ sheets.length !== 1 ? 's' : '' }}
          </small>
          <button class="btn btn-sm btn-outline-secondary" @click="fetchSheets">
            <i class="fa fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="sheets.length > 0" class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 flex-wrap px-3">
      <!-- Rows per page -->
      <div class="d-flex align-items-center gap-2">
        <span class="text-muted">Rows per page:</span>
        <select
          class="form-select form-select-sm rows-select"
          v-model.number="tablePagination.per_page"
          @change="changeRows"
        >
          <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
            {{ n }}
          </option>
        </select>
      </div>
      <!-- Pagination Buttons -->
      <div class="pagination-mini">
        <button
          class="page-btn prev_btn"
          :disabled="!tablePagination.has_prev"
          @click="handlePageChange(tablePagination.current_page - 1)"
        >
          «
        </button>
        <button
          v-for="(p, index) in visiblePages"
          :key="index"
          class="page-btn"
          :class="{
            'page-active': p === tablePagination.current_page,
            'disabled-dots': p === '...',
          }"
          :disabled="p === '...'"
          @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
        >
          {{ p }}
        </button>
        <button
          class="page-btn next_btn"
          :disabled="!tablePagination.has_next"
          @click="handlePageChange(tablePagination.current_page + 1)"
        >
          »
        </button>
      </div>
    </div>
  </div>

  <!-- Create/Edit Sheet Modal -->
  <div 
    v-if="showModal"
    class="modal fade show d-block" 
    tabindex="-1" 
    style="background-color: rgba(0,0,0,0.5);"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditMode ? 'Edit' : 'Create' }} Sheet
          </h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveSheet">
            <!-- Module Selection (only for create) -->
            <div v-if="!isEditMode" class="mb-3">
              <label for="moduleSelect" class="form-label">Select Module <span class="text-danger">*</span></label>
              <select 
                id="moduleSelect"
                class="form-select" 
                v-model="formData.workspace_module_id"
                required
              >
                <option value="">-- Select Module --</option>
                <option 
                  v-for="module in defaultModules" 
                  :key="module._id" 
                  :value="module._id"
                >
                  {{ module.variables?.['module-title'] || module.title || 'Untitled Module' }}
                </option>
              </select>
            </div>

            <!-- Sheet Title -->
            <div class="mb-3">
              <label for="sheetTitle" class="form-label">Sheet Title <span class="text-danger">*</span></label>
              <input 
                type="text" 
                class="form-control" 
                id="sheetTitle"
                v-model="formData.variables['sheet-title']"
                placeholder="Enter sheet title"
                required
              />
            </div>

            <!-- Sheet Description -->
            <div class="mb-3">
              <label for="sheetDescription" class="form-label">Sheet Description</label>
              <textarea 
                class="form-control" 
                id="sheetDescription"
                v-model="formData.variables['sheet-description']"
                rows="3"
                placeholder="Enter sheet description"
              ></textarea>
            </div>

            <!-- Sheet Icon -->
            <div class="mb-3">
              <label class="form-label">Sheet Icon</label>
              
              <!-- Use IconUploader component -->
              <IconUploader 
                v-model="formData.variables['sheet-icon']"
              />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="saveSheet"
            :disabled="isSaving || !formData.variables['sheet-title']?.trim()"
          >
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isSaving ? 'Saving...' : (isEditMode ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import apiService from '@/services/apiService';
// import zeroConfiguration from '@/components/commen/table/zeroConfiguration.vue';
import CommonTable2 from '@/components/commen/table/CommonTable2.vue';
import { usePermissions } from '@/composables/usePermissions';

const { canCreate } = usePermissions();

// Define IconUploader as async component
const IconUploader = defineAsyncComponent({
  loader: () => import('@/components/commen/modal/IconUploader.vue'),
  loadingComponent: {
    template: `
      <div class="text-center py-3">
        <div class="spinner-border spinner-border-sm" role="status"></div>
        <span class="ms-2">Loading icon picker...</span>
      </div>
    `
  },
  errorComponent: {
    template: `
      <div class="alert alert-warning">
        <small>Icon picker unavailable. Using manual selection.</small>
      </div>
    `
  },
  delay: 200,
  timeout: 3000
});

// Types
interface Sheet {
  _id: string;
  icon: {
    prefix: string;
    iconName: string;
    _id?: string;
  } | null;
  variables: {
    'sheet-title': string;
    'sheet-description': string;
    'sheet-icon'?: {
      prefix: string;
      iconName: string;
    };
  };
  workspace_id: string;
  workspace_module_id: string;
  is_ai_generated: boolean;
  created_at: string;
  updated_at: string | null;
  module: {
    _id: string;
    variables: {
      'module-title': string;
      'module-icon'?: {
        prefix: string;
        iconName: string;
      };
    };
    is_default: boolean;
  };
  workspace: {
    _id: string;
    title: string;
  };
  created_by: {
    _id: string;
    name: string;
    email: string;
  };
}

interface Module {
  _id: string;
  title?: string;
  variables?: {
    'module-title'?: string;
  };
}

// Reactive state
const activeTab = ref<'default' | 'byusers'>('default');
const isLoading = ref(false);
const error = ref<string | null>(null);
const sheets = ref<Sheet[]>([]);
const defaultModules = ref<Module[]>([]);
const showModal = ref(false);
const isEditMode = ref(false);
const isSaving = ref(false);
const editingSheetId = ref<string | null>(null);

// --- Pagination State ---
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Form data
const formData = ref({
  workspace_module_id: '',
  variables: {
    'sheet-title': '',
    'sheet-description': '',
    'sheet-icon': null as { prefix: 'fa-solid' | 'fa-regular' | 'fa-brands' | 'fa-light' | 'fa-thin' | 'fa-duotone' | 'fa-sharp-solid' | 'fa-sharp-duotone'; iconName: string } | null
  }
});

// Computed properties
const currentTabTitle = computed(() => 
  activeTab.value === 'default' ? 'Default Sheets' : 'By Users Sheets'
);

const currentSheetIcon = computed(() => formData.value.variables['sheet-icon']);

// Table configuration for zeroConfiguration component
const tableConfig = computed(() => ({
  data: processedSheets.value,
  columns: [
    { name: "Icon", field: "display_icon", sortable: false },
    { name: "Title", field: "sheet_title", sortable: true },
    { name: "Description", field: "sheet_description", sortable: false },
    { name: "Module", field: "module_title", sortable: true },
    { name: "Workspace", field: "workspace_title", sortable: true },
    { name: "Created By", field: "created_by_name", sortable: true },
    { name: "Created", field: "created_at", sortable: true, type: "date" },
  ],
  actions: activeTab.value === 'default' ? [true] : [false], // Only show actions for default tab
  loading: isLoading.value,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",
}));

const changeRows = async () => {
  tablePagination.value.current_page = 1;
  await fetchSheets(1, tablePagination.value.per_page);
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (page > tablePagination.value.total_pages) return;

  await fetchSheets(page, tablePagination.value.per_page);
};

// Process sheets data for table display
const processedSheets = computed(() => 
  sheets.value.map(sheet => ({
    ...sheet,
    display_icon: sheet.variables?.['sheet-icon']?.iconName 
      ? `<i class="${sheet.variables['sheet-icon'].prefix || 'fa'} ${sheet.variables['sheet-icon'].iconName}"></i>`
      : '<i class="fa fa-file-alt text-muted"></i>',
    sheet_title: sheet.variables?.['sheet-title'] || 'Untitled Sheet',
    sheet_description: sheet.variables?.['sheet-description'] || 'No description',
    module_title: sheet.module?.variables?.['module-title'] || 'Unknown Module',
    workspace_title: sheet.workspace?.title || 'Unknown Workspace',
    created_by_name: sheet.created_by?.name || 'Unknown User'
  }))
);

// Methods
const setActiveTab = (tab: 'default' | 'byusers') => {
  activeTab.value = tab;
  tablePagination.value.current_page = 1;
  fetchSheets(1, tablePagination.value.per_page);
};

const fetchSheets = async (page = 1, limit = 10) => {
  isLoading.value = true;
  error.value = null;
  
  try {
    console.log(`Fetching ${activeTab.value} sheets...`);
    const isDefault = activeTab.value === 'default';
    const endpoint = `common/with-module-info?is_default=${isDefault}`;
    const response = await apiService.get(endpoint, { page, limit });
    console.log('Sheets API Response:', response);
    console.log('Response type:', typeof response);
    console.log('Response keys:', response ? Object.keys(response) : 'null/undefined');
    
    // Handle multiple possible response formats
    let sheetsData: Sheet[] = [];
    
    if (response && response.data && Array.isArray(response.data.sheets)) {
      // Format: { status, message, data: { sheets: [...] } }
      sheetsData = response.data.sheets;
    } else if (response && Array.isArray(response.sheets)) {
      // Format: { sheets: [...] }
      sheetsData = response.sheets;
    } else if (Array.isArray(response)) {
      // Format: [...]
      sheetsData = response;
    } else if (Array.isArray(response.data)) {
      // Format: { data: [...] }
      sheetsData = response.data;
    } else {
      console.error('Unexpected response structure:', response);
      console.error('Response data:', response?.data);
      throw new Error(`Invalid response format. Got: ${typeof response}, expected sheets array`);
    }
    
    sheets.value = sheetsData;
    console.log(`Loaded ${sheets.value.length} ${activeTab.value} sheets`);

    // backend gives pagination object
    if (response.pagination) {
      tablePagination.value.current_page = response.pagination.current_page || page;
      tablePagination.value.total_pages = response.pagination.total_pages || 1;
      tablePagination.value.total = response.pagination.total || sheetsData.length;
      tablePagination.value.has_prev = (response.pagination.current_page || page) > 1;
      tablePagination.value.has_next =
        (response.pagination.current_page || page) < (response.pagination.total_pages || 1);
    } else {
      // Fallback if no pagination metadata
      tablePagination.value.current_page = page;
      tablePagination.value.total_pages = 1;
      tablePagination.value.total = sheetsData.length;
      tablePagination.value.has_prev = false;
      tablePagination.value.has_next = false;
    }
  } catch (err: any) {
    console.error('Error fetching sheets:', err);
    error.value = err?.response?.data?.message || err?.message || 'Failed to load sheets';
    sheets.value = [];
  } finally {
    isLoading.value = false;
  }
};

const fetchDefaultModules = async () => {
  try {
    console.log('Fetching default modules for sheet creation...');
    const response = await apiService.get('/common/workspace-modules-filter', { 
      is_default: true 
    });
    
    if (Array.isArray(response)) {
      defaultModules.value = response;
      console.log(`Loaded ${defaultModules.value.length} default modules`);
    }
  } catch (err: any) {
    console.error('Error fetching default modules:', err);
  }
};

const openCreateModal = () => {
  isEditMode.value = false;
  editingSheetId.value = null;
  resetForm();
  showModal.value = true;
  fetchDefaultModules();
};

const resetForm = () => {
  formData.value = {
    workspace_module_id: '',
    variables: {
      'sheet-title': '',
      'sheet-description': '',
      'sheet-icon': {
        prefix: 'fa-solid',
        iconName: ''
      }
    }
  };
};

const ensureIconObject = () => {
  if (!formData.value.variables['sheet-icon']) {
    formData.value.variables['sheet-icon'] = {
      prefix: 'fa-solid',
      iconName: ''
    };
  }
};

const updateIconPrefix = (event: Event) => {
  ensureIconObject();
  const target = event.target as HTMLSelectElement;
  if (formData.value.variables['sheet-icon']) {
    formData.value.variables['sheet-icon'].prefix = target.value as any;
  }
};

const updateIconName = (event: Event) => {
  ensureIconObject();
  const target = event.target as HTMLInputElement;
  if (formData.value.variables['sheet-icon']) {
    formData.value.variables['sheet-icon'].iconName = target.value.startsWith('fa-') 
      ? target.value 
      : `fa-${target.value}`;
  }
};

const closeModal = () => {
  showModal.value = false;
  isEditMode.value = false;
  editingSheetId.value = null;
  resetForm();
};

const saveSheet = async () => {
  if (!formData.value.variables['sheet-title']?.trim()) {
    alert('Please enter a sheet title');
    return;
  }

  if (!isEditMode.value && !formData.value.workspace_module_id) {
    alert('Please select a module');
    return;
  }

  isSaving.value = true;
  
  try {
    const payload = {
      ...formData.value,
      // Clean up empty icon
      variables: {
        ...formData.value.variables,
        'sheet-icon': formData.value.variables['sheet-icon']?.iconName 
          ? formData.value.variables['sheet-icon'] 
          : undefined
      }
    };

    if (isEditMode.value && editingSheetId.value) {
      await apiService.put('/common/cms-sheets', editingSheetId.value, payload);
      console.log('Sheet updated successfully');
    } else {
      await apiService.post('/common/cms-sheets', payload);
      console.log('Sheet created successfully');
    }
    
    closeModal();
    await fetchSheets(tablePagination.value.current_page, tablePagination.value.per_page);
  } catch (err: any) {
    console.error('Error saving sheet:', err);
    alert('Failed to save sheet: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
  } finally {
    isSaving.value = false;
  }
};

// Event handlers for zeroConfiguration table
const handleEdit = (sheet: any) => {
  if (activeTab.value !== 'default') {
    console.log('Edit action not allowed for By Users sheets');
    return;
  }
  
  isEditMode.value = true;
  editingSheetId.value = sheet._id;
  
  formData.value = {
    workspace_module_id: sheet.workspace_module_id,
    variables: {
      'sheet-title': sheet.variables?.['sheet-title'] || '',
      'sheet-description': sheet.variables?.['sheet-description'] || '',
      'sheet-icon': sheet.variables?.['sheet-icon'] || {
        prefix: 'fa',
        iconName: ''
      }
    }
  };
  
  showModal.value = true;
};

const handleView = (sheet: any) => {
  console.log('Viewing sheet:', sheet);
  alert(`Sheet: ${sheet.sheet_title}\nModule: ${sheet.module_title}\nWorkspace: ${sheet.workspace_title}\nCreated by: ${sheet.created_by_name}`);
};

const handleDelete = async (id: string) => {
  if (activeTab.value !== 'default') {
    console.log('Delete action not allowed for By Users sheets');
    return;
  }
  
  if (!confirm("Are you sure you want to delete this sheet?")) return;
  
  try {
    console.log('Deleting sheet:', id);
    await apiService.delete('/common/cms-sheets', id);
    
    await fetchSheets(tablePagination.value.current_page, tablePagination.value.per_page);
    console.log('Sheet deleted successfully');
  } catch (err: any) {
    console.error('Error deleting sheet:', err);
    alert('Failed to delete sheet: ' + (err?.message || 'Unknown error'));
  }
};

// Lifecycle
onMounted(() => {
  fetchSheets(tablePagination.value.current_page, tablePagination.value.per_page);
});
</script>

<style scoped>
/* Tab styling */
 .nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
  color: #6c757d;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  background-color: transparent;
  border-color: transparent transparent #35bfbf transparent;
  color: #35bfbf;
  font-weight: 700;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent;
  color: #495057;
}

/* Card styling */
.card-title {
  font-weight: 600;
  color: #2c3e50;
}

/* Icon preview styling */
.icon-preview {
  width: 60px;
  height: 60px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
}

/* Modal styling */
.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }
  
  .icon-preview {
    width: 50px;
    height: 50px;
  }
}

.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}

.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}

.page-active:hover {
  background: #21c1d6;
}

.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>